<template>
  <form role="form" @submit="onFormSubmit" name="availability">
    <div class="availability-form">
      <div class="row" v-if="availabilityForm.title">
        <div class="col-lg-12">
          <h2>{{getLocalizedText(availabilityForm.title)}}</h2>
        </div>
      </div>
      <div class="row" v-if="allowCreateFrom">
        <div class="col-lg-12">
          <h3>{{$t('shop.availabilities.create-from.title')}}</h3>
        </div>
      </div>
      <Select 
        v-if="allowCreateFrom"
        :selectOptions="{
          options: createFromActivities,
          getOptionLabel : activity => getLocalizedText(activity.title),
          reduce : activity => activity
        }"
        v-bind:value.sync="createFromForm.activity" 
        id="createFromActivity" 
        :label="$t('shop.availabilities.create-from.activity')" 
        :placeholder="$t('shop.availabilities.create-from.activity_placeholder')"
        labelAsColumn 
      >
      </Select>
      <Select 
        v-if="allowCreateFrom"
        :selectOptions="{
          options: createFormAvailabilities,
          getOptionLabel : availability => getLocalizedText(availability.title),
          reduce : availability => availability
        }"
        v-bind:value.sync="createFromForm.availability" 
        id="createFromAvailability" 
        :label="$t('shop.availabilities.create-from.availability')" 
        :placeholder="$t('shop.availabilities.create-from.availability_placeholder')"
        labelAsColumn 
      >
        <template v-slot:buttons>
            <button class="btn btn-primary create-from" type="button" @click="createFromAvailability()">{{ $t('shop.availabilities.create-from.button') }}</button>
        </template>
      </Select>
      <div class="row">
        <div class="col-lg-12">
          <h3>{{$t('shop.availabilities.info.information')}}</h3>
        </div>
      </div>
      <LocalizedText 
        v-bind:value.sync="availabilityForm.title" 
        id="updateTitle" 
        :label="$t('shop.availabilities.info.title')" 
        :placeholder="$t('shop.availabilities.info.title_placeholder')" 
        :labelAsColumn="true"
        :required="false"
        :languageCodes="availableLanguageCodes"
        :rowClass="{'form-group':true, 'row':true, 'update-title':true}"
      />
      <div class="hr-line-dashed"></div>
      <DateInput 
        v-bind:value.sync="availabilityForm.startDate" 
        :label="$t('shop.availabilities.info.startDate')" 
        required 
        labelAsColumn 
      />
      <DateInput 
        v-bind:value.sync="availabilityForm.endDate" 
        :label="$t('shop.availabilities.info.endDate')" 
        required 
        labelAsColumn 
      />
      <div class="hr-line-dashed"></div>
      <TextField 
        v-bind:value.sync="availabilityForm.minDaysForAvailability" 
        :label="$t('shop.availabilities.info.minDaysForAvailability')" 
        :placeholder="$t('shop.availabilities.info.minDaysForAvailability_placeholder')" 
        fieldType="number"
        labelAsColumn 
      />
      <TextField 
        v-bind:value.sync="availabilityForm.maxDaysForAvailability" 
        :label="$t('shop.availabilities.info.maxDaysForAvailability')"
        :placeholder="$t('shop.availabilities.info.maxDaysForAvailability_placeholder')"  
        fieldType="number"
        labelAsColumn 
      />
      <div class="hr-line-dashed"></div>
      <SlotsPerWeekdayInput
        v-bind:value.sync="availabilityForm.slotsPerWeekday"
      />
    </div>
    <button class="btn btn-primary ladda-button availability" data-style="zoom-in" type="submit">{{buttonLabel}}</button>
  </form>
</template>

<style>
  
</style>

<script lang="ts">
import { toRefs, Ref, defineComponent, PropType, computed, onMounted, onBeforeUnmount, ref, watch, reactive } from '@fwk-node-modules/vue'
import { getApp, useRouter, useStore } from '@fwk-client/utils/vue-3-migration';
import * as Ladda from 'ladda';

import SlotsPerWeekdayInput from './SlotsPerWeekdayInput.vue';
import LocalizedText from '@root/src/client/components/panels/input/LocalizedText.vue';
import DateInput from '@root/src/client/components/panels/input/DateInput.vue';
import SwitchCheck from '@fwk-client/components/panels/input/SwitchCheck.vue';
import Select from '@fwk-client/components/panels/input/Select.vue';
import TextField from '@fwk-client/components/panels/input/TextField.vue';

import { useProductAdmin } from '../../../composables/useProductAdmin';
import { useAvailabilityAdmin } from '../../../composables/useAvailabilityAdmin';
import { useShopAdmin } from '../../../composables/useShopAdmin';


export default defineComponent({
  props: {
      availability: {
        type: Object as PropType<any>,
        required: false
      }
  },
  components: {
    DateInput,
    SwitchCheck,
    LocalizedText,
    Select,
    SlotsPerWeekdayInput,
    TextField
  },
  setup(props, context) {
    const app = getApp();
    const $router = useRouter();
    const $store = useStore();

    const { selectedShop } = useShopAdmin(props, context)
    const { products }  = useProductAdmin({
        mode: "ACTIVITY"
      }, context);
    const { availabilityForm, updateAvailabilityFormForUpdate, updateAvailability, createAvailability, getShopAvailabilities  } = useAvailabilityAdmin({}, context);

    const availableLanguageCodes = ['fr','en'];

    const { availability } = toRefs(props);

    const createFromForm:Ref<any> = ref({});
    const createFormAvailabilities:Ref<any> = ref([]);

    if(props.availability) {
      updateAvailabilityFormForUpdate(props.availability);
    }

    const buttonLabel = computed(() => {
      if(availability.value) { return app.$t('shop.availabilities.update.button') }
      else { return 'Create'; }
    })

    var laddaSubmit:Ladda.LaddaButton|null = null;

    onMounted(() => {
      var button:HTMLButtonElement|null = document.querySelector( 'form[name=availability] button.ladda-button.availability' );
      laddaSubmit = Ladda.create(button!);
    })

    const allowCreateFrom = props.availability == undefined;

    const createFromAvailability = () => {
      if(createFromForm.value.availability) {
          updateAvailabilityFormForUpdate(createFromForm.value.availability);
      }
    }

    const createFromActivities = computed(() => {
      return products.value;
    })

    const onFormSubmit = (evt:Event) => {
      // We prevent submit of the page
      evt.preventDefault();

      laddaSubmit!.start();

      if(availability.value) {
        updateAvailability().then((result:any) => {
          if(result.updated) {
            context.emit('availability-updated', result.availability);
          }
          laddaSubmit!.stop();
        })
      }
      else {
        createAvailability().then((result:any) => {
          if(result.created) {
            context.emit('availability-created', result.availability);
          }
          laddaSubmit!.stop();
        })        
      }
    }

    watch(
      availability,
      (val:any, oldVal:any) => {
        // We update the form with new availability
        updateAvailabilityFormForUpdate(availability.value);
      },
      { deep: true }
    )

    watch(
      () => createFromForm.value.activity,
      (val:any, oldVal:any) => {
        if(createFromForm.value.activity) {
          // We update the list of availabilities
          getShopAvailabilities(selectedShop.value.shop._id, createFromForm.value.activity._id).then((response) => {
            createFormAvailabilities.value = response.availabilities;
            createFromForm.value.availability = undefined;
          });
        }
        else {
          createFormAvailabilities.value = [];
        }
      },
      { deep: true }
    )

    return {
      availability,
      availableLanguageCodes,
      availabilityForm,
      buttonLabel,
      onFormSubmit,
      allowCreateFrom,
      createFromAvailability,
      createFormAvailabilities,
      createFromActivities,
      createFromForm
    }
  }
})
</script>